/**
 * #4981 phase 3 (#5269) — copying an Android device's local data from the browser's
 * IndexedDB into the native SQLite engine.
 *
 * **This is the phase where a mistake costs a user their logged workouts.** Read
 * `design-docs/android-local-database-engine.md`, "Moving the data across (phase 3)",
 * before changing anything here.
 *
 * Three things hold that risk down, and none of them is optional:
 *
 *  - **The copy is PouchDB's own replication, never a hand-rolled read-and-write.**
 *    Reading every document with `allDocs` and writing them across silently drops
 *    three things: an offline deletion that has not reached the server yet would be
 *    RESURRECTED on the next sync, conflicting edits the app's conflict machinery
 *    depends on would be flattened to one, and revision history would be lost.
 *    `sqlite-migration.test.ts` proves replication carries all three into this
 *    adapter.
 *  - **Nothing is destroyed.** The IndexedDB data stays exactly where it is. The
 *    engine marker moves only after every database has been copied AND verified
 *    document for document. So a crash, a jammed store or a mismatch leaves the
 *    device precisely where it started, and the next boot tries again. Phase 4
 *    deletes the old copy, after a grace period.
 *  - **The copy is verified before it is believed**, and the verification compares
 *    the full set of leaf revisions rather than "the revision" — see R6.
 *
 * Angular-free and driven by injected dependencies, the same shape as
 * `legacy-idb-cleanup.ts`, so the whole thing can be driven from a node test. Every
 * JUDGEMENT it makes lives in `services/sqlite-migration-decisions.ts`, which imports
 * nothing.
 *
 * **What it must NOT be merged with.** `legacy-idb-cleanup.ts` enumerates databases
 * the same way, and they do opposite things: that one DELETES IndexedDB, this one
 * READS it. Putting them together, one phase before deletion becomes real, is how a
 * mistake here becomes unrecoverable.
 */

import { errorReportFields } from '../services/error-patterns';
import { listPouchIndexedDbNamesAsync } from './legacy-idb-cleanup';
import { resolveConfigRecordForUser } from '../services/config-record-resolve';
import { GLOBAL_VIEW_REBUILD_VERSION } from '../services/sync-view-heal-decisions';
import { stripLocalStoreGeneration } from '../services/local-store-generation';
import { localCoachClientDbBaseName, localUserDbBaseName } from '../services/local-user-db-name';
import { isLocalStoreNotAnsweringError } from '../services/local-store-timeout';
import {
  DocumentLeafSummary,
  MigrationOutcome,
  MigrationPhase,
  MigrationTargetState,
  VerificationResult,
  isLocalDocDeliberatelyNotCopied,
  readMigrationTargetState,
  recordMigrationTargetName,
  shouldDeleteExistingTarget,
  verifyDatabaseContents,
  verifyLocalDocuments,
} from '../services/sqlite-migration-decisions';
import { SQLITE_MIGRATION_TARGETS_KEY } from '../services/local-database-engine-decisions';

/** The IndexedDB adapter, named EXPLICITLY.
 *
 *  Two reasons it is spelled out rather than left to `getLocalAdapterOpts()`. First,
 *  that helper asks the engine question, and R3 depends on nothing having asked it
 *  yet. Second, the name matters: PouchDB also ships a NEWER adapter called
 *  `indexeddb`, which stores its data under a different structure. Naming that one
 *  would open a brand-new EMPTY database on every device — the copy would then find
 *  nothing, and the failure would look like a config problem rather than a typo. The
 *  `_pouch_` prefix R4 enumerates is what `idb` writes. */
const IDB_ADAPTER = 'idb';

/** The native SQLite adapter, named explicitly for the same reason. */
const SQLITE_ADAPTER = 'capacitor-sqlite';

/**
 * Which way a copy runs. (#4981 phase 4, #5270)
 *
 * Phase 3 only ever copied IndexedDB into SQLite, so both adapters and the target's name
 * were written into this module as constants. Phase 4 copies back the other way, and the
 * machinery that makes a copy safe — the kept partial target, the slice pacing, the
 * verification, the `_local/` copy, the follow — is the same machinery in both
 * directions. So the direction is threaded through rather than the module being
 * duplicated.
 *
 * **This is NOT the direction-agnostic copier the phase-4 plan forbids.** That would be
 * one function whose failure handling, triggers and risk were parameterised away; the two
 * directions genuinely differ there, and they stay in separate modules with separate
 * outcomes, separate budgets and separate telemetry names. What is shared is the copying
 * itself, and these are the four things about it that are not the same.
 */
export interface CopyDirection {
  /** the adapter the data is read FROM */
  sourceAdapter: string;
  /** the adapter the data is written TO */
  targetAdapter: string;
  /**
   * The name to write into, given the source's logical name.
   *
   * Forward this is the SAME name on the other engine. Backward it is the NEXT #4401
   * generation of it, because the pre-migration IndexedDB databases still exist under
   * exactly the names a reverse copy would otherwise write to — and the grace period
   * exists to keep them.
   */
  targetNameFor(sourceLogicalName: string): string;
  /**
   * Throw away a kept target that can no longer vouch for itself.
   *
   * Direction-specific because the two engines are deleted by different means: a native
   * plugin call on the SQLite side, and the browser's own database deletion on the
   * IndexedDB side.
   */
  deleteTargetDatabaseAsync(targetLogicalName: string): Promise<unknown>;
  /**
   * Record that a target now exists, so a teardown can delete it; `false` fails the copy
   * closed. Optional, because only one direction needs it.
   *
   * Forward it is `SQLITE_MIGRATION_TARGETS_KEY`: SQLite files are invisible to sign-out,
   * which is handed a list of names because the native plugin cannot list its own files.
   * Backward there is nothing to record — the teardown enumerates and deletes every
   * IndexedDB database on the origin, so a reverse target is swept like any other.
   */
  recordTargetName?(targetLogicalName: string): boolean;
  /**
   * The two problem names the LIVE FOLLOW reports under, when this direction wants its own.
   *
   * Absent means the migration's pair, which is what phase 3 hard-coded. The follow is the
   * one piece of shared machinery that reports on its own — everything else hands its
   * outcome back for the caller to name — so without this a rollback's follow failures
   * would be filed under `AndroidSqliteMigration*` and counted as migration trouble. The
   * ramp reads the two apart on purpose: they mean opposite things about how the rollout is
   * going.
   */
  followProblemNames?: { failed: string; denied: string };
}

/**
 * How long one source read or one replication batch may take.
 *
 * Deliberately NOT `withBootLocalStoreTimeout`, whose fixed 15 seconds exists for
 * "a single `_local/` document read, or one `bulkDocs` of the boot singletons". A
 * real database copy routinely exceeds that, so bounding the WHOLE copy at it would
 * time out on every real device and exhaust the retry budget in three boots.
 *
 * The bound is therefore PER read or PER batch, and this is the limit for each.
 *
 * **The COPY measures this differently, and #5448 is why.** The object PouchDB hands
 * back for a replication settles only once the whole replication has finished, so a
 * limit on how long it takes is a limit on the whole copy — which on a real account is
 * about five minutes, so every device was cancelled at 60 seconds and none could ever
 * migrate. The copy therefore goes through `withLocalStoreIdleTimeout`, where this same
 * number limits how long the copy may go SILENT: every progress signal starts it again.
 * One number, two meanings — "how long this call may take" for a read, "how long
 * without a sign of life" for the copy.
 */
export const MIGRATION_BATCH_TIMEOUT_MS = 60_000;

/** How long the bounded config re-read may take. This one IS a single small document
 *  read, which is exactly the size of operation the boot bound was written for. */
export const MIGRATION_CONFIG_READ_TIMEOUT_MS = 15_000;

/** How many documents a verification or attachment-scan batch reads at once.
 *  Bounded because this adapter attaches a full document body to every change, and an
 *  unbatched pass would read a whole user database into memory at boot, twice. */
export const MIGRATION_SCAN_BATCH_SIZE = 200;

/**
 * How many documents one BACKGROUND replication batch carries. (#5449)
 *
 * Deliberately smaller than {@link MIGRATION_SCAN_BATCH_SIZE}, because this batch is
 * written while somebody is using the app rather than while they watch a boot screen.
 * Each batch is an uninterrupted run of work on the same thread the interface draws
 * on, so the size of a batch is the size of the longest stall a user can feel.
 */
export const MIGRATION_BACKGROUND_BATCH_SIZE = 50;

/**
 * How many batches one background slice writes before it pauses. (#5449)
 *
 * The copy is cut into slices with a pause between them so it cannot occupy the app
 * for the several minutes it takes. Slices are counted in BATCHES WRITTEN, not in
 * progress signals, and that distinction is load-bearing: PouchDB emits a progress
 * signal per document READ or COMPARED, so a slice counted in those could end without
 * ever writing anything — and a copy that writes nothing per slice never finishes,
 * however many slices it runs.
 */
export const MIGRATION_SLICE_BATCHES = 5;

/** How many batches the background copy may hold in memory at once. (#5449)
 *
 *  PouchDB reads ten batches ahead by default and keeps them. The image outbox carries
 *  photo bytes inside its documents, so ten batches of those is a large amount of
 *  memory to be holding on a low-end phone while somebody is using the app. One batch
 *  at a time costs a little throughput on a copy that is deliberately slow anyway. */
export const MIGRATION_BACKGROUND_BATCHES_LIMIT = 1;

/**
 * How long the background copy waits between slices. (#5449)
 *
 * Long enough that the copy is plainly taking its turn rather than competing with the
 * app, short enough that a copy of a real account still finishes within a session or
 * two. It is a pause between slices, not a budget: the app can extend it indefinitely
 * by staying hidden, which is exactly what it does when somebody puts their phone down.
 */
export const MIGRATION_SLICE_PAUSE_MS = 2_000;

/**
 * How many times the background verification may re-check and try again. (#5449)
 *
 * The source is LIVE while the bulk copy runs — that is the entire point of running it
 * in the background — so by the time the copy finishes, the source has moved on from the
 * snapshot the verification started with. Those differences are not damage; they are the
 * user getting on with their day. Each round carries across what moved and re-checks only
 * what still differs.
 *
 * A small number on purpose. It converges immediately on any device where somebody is not
 * writing continuously, and a device where they ARE should stop and let the next
 * background pass finish the job rather than chase a moving target for ever.
 */
export const MIGRATION_VERIFY_RECONCILE_ROUNDS = 3;

/** The `_local/` document in which a target records what has been copied into it. (#5449) */
export const MIGRATION_STATE_DOC_ID = '_local/cs-migration-state';

/** Config document ids the migrating boot re-reads, for R2's kill switch. */
export const MIGRATION_ALLOW_LIST_DOC_ID = 'config/AndroidSqliteMigrationAllowList';
export const MIGRATION_PERCENT_DOC_ID = 'config/AndroidSqliteMigrationPercent';

/** The `_local/` document holding the view-index heal salts and the rebuild stamp. */
const VIEW_HEAL_DOC_ID = '_local/view-heal';

/** A PouchDB database handle, as much of one as this module uses. */
export interface MigrationDb {
  get(id: string, opts?: any): Promise<any>;
  put(doc: any, opts?: any): Promise<any>;
  allDocs(opts: any): Promise<any>;
  changes(opts: any): any;
  replicate: { to(target: MigrationDb, opts?: any): any };
  /** how many live documents this database holds, and its update sequence (#5449) */
  info(): Promise<any>;
  /**
   * The database's own persisted id. (#5449)
   *
   * NOT available from `info()`, which reports `db_name` — the name, which is the same
   * string before and after a database is destroyed and recreated. The id is what tells
   * those apart, and telling them apart is the whole point: signing out and back in
   * rebuilds the source under its old name.
   */
  id(): Promise<string>;
  close(): Promise<any>;
  destroy(): Promise<any>;
}

/**
 * What BOTH halves of the migration need from the outside world. Each is injected so
 * the whole thing runs under node with no browser and no Capacitor.
 *
 * Nothing here is phase-specific. What one half needs and the other does not lives on
 * {@link SqliteBackgroundMigrationDeps} or {@link SqliteSwitchMigrationDeps}, and
 * callers are handed the union {@link SqliteMigrationDeps}.
 *
 * Exported so a builder can assemble this shared part once and then add the phase's own
 * members to it — see `buildSqliteMigrationDeps` in `app.component.ts`. Nothing takes
 * this type as a parameter: the migration always wants a phase.
 */
export interface SqliteMigrationDepsCore {
  /** enumerate the origin's IndexedDB */
  indexedDb: { databases(): Promise<{ name?: string }[]> };
  /**
   * Which way this copy runs. (#5270) Absent means the FORWARD direction — IndexedDB
   * into SQLite, same name on both sides — which is what every phase-3 caller wants and
   * what {@link resolveCopyDirection} fills in.
   */
  direction?: CopyDirection;
  /** open a PouchDB handle by name and adapter */
  openDatabase(name: string, adapter: string): MigrationDb;
  /** delete a SQLite database outright, used to clear a partial target before a retry */
  deleteSqliteDatabaseAsync(name: string): Promise<unknown>;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  /** the signed-in user */
  uid: string;
  /** the client this device's coach is managing, when it is a coach device */
  managedClientUid: string | undefined;
  /** this device's id, for the percentage bucket */
  deviceId: string | undefined;
  /** the rollout decision, injected so the pure helper stays in the decisions module */
  decideFromControls(allowListValue: unknown, percentValue: unknown): boolean;
  /**
   * Which two config documents {@link decideFromControls} is handed. (#5270)
   *
   * Absent means the migration's pair. The rollback reads a different pair, and its
   * `decideFromControls` is a different pure helper — so the two travel together and
   * neither can be swapped without the other.
   */
  controlDocIds?: { allowList: string; percent: string };
  /** turn a base database name into this device's current generation of it (#4401) */
  currentLocalStoreName(baseName: string): string;
  reporter: MigrationReporter;
  /** bound an operation. Injected so a test does not wait a real minute. */
  withTimeout<T>(operation: Promise<T>, operationName: string, timeoutMs: number): Promise<T>;
  /**
   * Bound an operation by how long it goes QUIET rather than by how long it takes
   * in total (#5448). `subscribeToProgress` is handed a callback and returns a
   * function that stops listening.
   *
   * Separate from `withTimeout` because the two measure different things and the
   * copy needs the second one: a real account takes minutes, so a total-time bound
   * cancels every copy on every device.
   */
  withIdleTimeout<T>(
    operation: PromiseLike<T>,
    operationName: string,
    idleTimeoutMs: number,
    subscribeToProgress: (onProgress: () => void) => () => void,
  ): Promise<T>;
  /**
   * Has anything asked the engine question yet on this page load? (#5448)
   *
   * Asked AGAIN after the copy, not only before it. The answer can change while the
   * copy runs — anything that opens a database asks it — and a copy finished on a
   * page load that is already committed to IndexedDB must not be believed.
   */
  hasEngineBeenRead(): boolean;
  /** called as each database finishes, so the splash can show progress */
  onProgress?(done: number, total: number): void;

  /**
   * The app version now running, recorded with each target so a storage-format change
   * between releases makes the copy start again rather than be trusted. (#5449)
   */
  appVersion: string;
}

/**
 * The SWITCH half: the top of a later page load, where a copy the background half has
 * already made and verified is finished, verified again and committed. (#5449)
 *
 * It adds nothing to the core. It exists so that `phase` can be the discriminant of
 * {@link SqliteMigrationDeps}, which is what lets the background variant below REQUIRE
 * what only the background half needs.
 */
export interface SqliteSwitchMigrationDeps extends SqliteMigrationDepsCore {
  /** Which half is running. See {@link MigrationPhase}. */
  phase: MigrationPhase.Switch;
}

/**
 * The BACKGROUND half: the bulk copy, running for minutes while the app carries on
 * using IndexedDB. (#5449)
 *
 * The three members below are REQUIRED here and absent from the switch variant, because
 * the rule is not "usually supplied" — it is "the background half cannot run correctly
 * without them". Leaving one out used to compile and produce a copy that silently
 * never paused, or that could not be stopped when the app tore down; now it is a
 * compile error.
 */
export interface SqliteBackgroundMigrationDeps extends SqliteMigrationDepsCore {
  /** Which half is running. See {@link MigrationPhase}. */
  phase: MigrationPhase.Background;

  /**
   * Register something that stops an in-flight replication, and answer with a
   * function that unregisters it. (#5449)
   *
   * The background copy runs for minutes, so the likeliest moment for a sign-out or a
   * reload is while it is still going. The teardown deletes the SQLite targets and
   * wipes the record of their names; a replication still writing afterwards recreates
   * a file holding the PREVIOUS user's documents that nothing on the device can name
   * or delete. Cancelling is what closes that window, and only the caller knows when
   * the teardown is starting.
   */
  registerCancellable(cancel: () => void): () => void;

  /**
   * Has the app asked this copy to stop? (#5449)
   *
   * Checked between slices, so a copy gives up promptly rather than carrying on into
   * a teardown. Together with `registerCancellable` above it covers both halves of the
   * race: this one stops the NEXT slice, that one stops the slice already running.
   */
  isCancelled(): boolean;

  /**
   * Wait until it is a good moment to start the next slice of a background copy.
   * (#5449)
   *
   * Injected rather than written here so a test does not wait real seconds, and so the
   * rule about WHEN the app is willing to copy lives with the app rather than with the
   * copy. In production it waits a short while and, while the app is hidden, does not
   * return at all — which is how the copy stops when somebody puts their phone down.
   */
  pauseBetweenSlicesAsync(): Promise<void>;
}

/**
 * Everything the migration needs from the outside world, for whichever half is running.
 *
 * A discriminated union on `phase` rather than one bag with optional members: which
 * collaborators a half may go without is a correctness rule, and this is the shape that
 * lets the compiler carry it instead of a paragraph of prose. Code that only runs in one
 * half takes that half's type and reaches its members directly — see
 * {@link replicateInSlicesAsync}.
 */
export type SqliteMigrationDeps = SqliteSwitchMigrationDeps | SqliteBackgroundMigrationDeps;

/** The part of `LoggingService` this module needs. */
export interface MigrationReporter {
  trackSilentInfo(name: string, context: { [key: string]: any }): void;
  trackSilentProblem(name: string, context: { [key: string]: any }): void;
}

/** What one migration attempt did. */
export interface MigrationResult {
  outcome: MigrationOutcome;
  /** the underlying failure behind an abort, when there was one */
  error?: unknown;
  /** how many databases were copied and verified */
  databasesCopied: number;
  /** how many documents crossed, summed over the databases */
  documentsCopied: number;
  /**
   * The databases this run copied and verified, by logical name. (#5449)
   *
   * The background half hands these to the follow, so it follows exactly what was
   * verified rather than re-deriving the list and risking a different answer.
   */
  copiedDatabaseNames?: string[];
}

/** Telemetry name for a completed migration (R10). */
export const MIGRATION_COMPLETED_INFO = 'AndroidSqliteMigrationCompleted';

/** One problem name per cause, because each is a different repair (R10).
 *
 *  **Deliberately PARTIAL**: `Completed`, `ConfigSaysNo` and `NoSignedInUser` are not
 *  problems and have no name here. The type says so, so the two lookups that guard
 *  against a miss are visibly load-bearing rather than reading as dead code. */
export const MIGRATION_PROBLEM_NAMES: Readonly<Partial<Record<MigrationOutcome, string>>> = {
  [MigrationOutcome.LatchAlreadyRead]: 'AndroidSqliteMigrationLatchAlreadyRead',
  [MigrationOutcome.EnumerationFailed]: 'AndroidSqliteMigrationEnumerationFailed',
  [MigrationOutcome.OwnDatabaseMissing]: 'AndroidSqliteMigrationOwnDatabaseMissing',
  [MigrationOutcome.CommonDatabaseMissing]: 'AndroidSqliteMigrationCommonDatabaseMissing',
  [MigrationOutcome.ConfigReadFailed]: 'AndroidSqliteMigrationConfigReadFailed',
  [MigrationOutcome.AttachmentsFound]: 'AndroidSqliteMigrationAttachmentsFound',
  [MigrationOutcome.BoundHit]: 'AndroidSqliteMigrationBoundHit',
  [MigrationOutcome.VerificationMismatch]: 'AndroidSqliteMigrationVerificationMismatch',
  [MigrationOutcome.CopyFailed]: 'AndroidSqliteMigrationCopyFailed',
  [MigrationOutcome.LatchTakenDuringCopy]: 'AndroidSqliteMigrationLatchTakenDuringCopy',
};

/** Every console line this migration writes carries it. */
const LOG_PREFIX = 'SQLite migration (phase 3): ';

/**
 * Strip the `_pouch_` prefix IndexedDB database names carry.
 *
 * The enumeration answers with storage-level names (`_pouch_userdb-fr_abc`); PouchDB
 * is opened with the logical name (`userdb-fr_abc`) and adds the prefix itself.
 */
export function toLogicalDbName(indexedDbName: string): string {
  return indexedDbName.startsWith('_pouch_') ? indexedDbName.slice('_pouch_'.length) : indexedDbName;
}

/**
 * Does this database belong to somebody else? (R4)
 *
 * **Both qualifications matter and each closes a real hole.** "Keep only names
 * carrying this uid" would silently drop the two databases that carry no uid at all —
 * the image upload outbox and the coach staging database — which are exactly two of
 * the databases the enumeration exists to find. And a bare "skip a different uid"
 * would drop `userdb-coach_<clientUid>` on a coach device: the database that device is
 * actually using. It would not be copied, and the engine marker would still be
 * written, because "every database found" had been. The coach would come up on SQLite
 * with an empty client database.
 */
export function belongsToAnotherAccount(
  logicalName: string,
  uid: string,
  managedClientUid: string | undefined,
): boolean {
  // The generation suffix is taken off FIRST, through the helper that owns the format
  // — hand-writing the pattern here is what made an earlier version match `__gen<n>`
  // when the app writes `__g<n>`, so a reset device's own database read as another
  // account's and the device was blocked for good.
  //
  // The uid then runs to the END of what is left, and is NOT restricted to a character
  // class: a uid containing a character the class left out would read as a DIFFERENT
  // account, and on the user's own database that means coming up on SQLite with
  // nothing in it.
  const match = stripLocalStoreGeneration(logicalName).match(/-(?:fr|coach)_(.+)$/);
  // no uid in the name at all: the image outbox and the coach staging database. Keep.
  if (!match) return false;
  const nameUid = match[1];
  if (nameUid === uid) return false;
  if (managedClientUid && nameUid === managedClientUid) return false;
  return true;
}

/**
 * Read a whole changes feed as one summary per document id. (R6)
 *
 * Two precautions, and both are load-bearing rather than tidy:
 *
 *  - `return_docs: false`, because PouchDB otherwise accumulates every change AND
 *    this adapter attaches the full document body to each one. Without it, verifying
 *    a large user database would read every document body into memory, twice, on a
 *    low-end phone at boot.
 *  - a batched `limit` with a moving `since`, because with no limit PouchDB issues one
 *    unbounded query.
 *
 * `style: 'all_docs'` is what returns EVERY leaf rather than the winner, which is the
 * whole point of the comparison this feeds.
 */
async function summariseChangesAsync(
  db: MigrationDb,
  deps: SqliteMigrationDeps,
  operationName: string,
  sinceSeq: unknown = 0,
): Promise<Map<string, DocumentLeafSummary>> {
  const summary = new Map<string, DocumentLeafSummary>();
  // #5449: the switch half asks only for what changed after the sequence the
  // background half verified through. Everything before it was compared document for
  // document already, so re-walking it would put the cost this change removes from the
  // boot screen straight back onto it.
  let since: any = sinceSeq ?? 0;

  for (;;) {
    const batch = await deps.withTimeout(
      readChangesBatchAsync(db, since),
      operationName,
      MIGRATION_BATCH_TIMEOUT_MS,
    );
    for (const change of batch.changes) {
      summary.set(change.id, {
        leafRevs: change.changes.map((leaf: any) => leaf.rev).sort(),
        deleted: change.deleted === true,
      });
    }
    if (batch.changes.length === 0) break;
    since = batch.lastSeq;
  }
  return summary;
}

/** One batch of the changes feed, collected through the emitter.
 *
 *  The callback is an EMITTER EVENT, not an `onChange` option — PouchDB dropped the
 *  option form, and passing it is ignored with no error, which would report every
 *  database as empty and make the verification pass on a copy that carried nothing. */
function readChangesBatchAsync(db: MigrationDb, since: any): Promise<{ changes: any[]; lastSeq: any }> {
  return new Promise((resolve, reject) => {
    const changes: any[] = [];
    const feed = db.changes({
      since,
      style: 'all_docs',
      return_docs: false,
      limit: MIGRATION_SCAN_BATCH_SIZE,
    });
    feed.on('change', (change: any) => changes.push(change));
    feed.then(
      (result: any) => resolve({ changes, lastSeq: result?.last_seq }),
      (err: any) => reject(err),
    );
  });
}

/**
 * Does any document in this database carry an attachment? (R5)
 *
 * **Attachments must be SCANNED FOR, not caught.** The SQLite adapter has no
 * attachment support at all, and the failure is SILENT: a Blob serialises to an empty
 * object and the bytes are lost, replication reports success, and R6's comparison
 * passes because the revision is identical. Nothing raises. Replication itself cannot
 * detect this, which is why the scan exists.
 *
 * It checks winning revisions through `allDocs`, and every NON-WINNING leaf through
 * `get(id, { rev })` — a conflicting branch can carry an attachment too, and the
 * changes feed alone cannot show it, because the feed returns revision IDS, not
 * bodies.
 *
 * Batched exactly as the verification read is: this runs on every database before the
 * copy, so an unbatched pass would read every document body of a large user database
 * at boot on a low-end phone.
 */
async function findAttachmentsAsync(
  db: MigrationDb,
  deps: SqliteMigrationDeps,
  dbName: string,
  leafSummary: Map<string, DocumentLeafSummary>,
): Promise<number> {
  let found = 0;
  let startkey: string | undefined;

  for (;;) {
    const page = await deps.withTimeout(
      db.allDocs({
        include_docs: true,
        limit: MIGRATION_SCAN_BATCH_SIZE,
        ...(startkey === undefined ? {} : { startkey, skip: 1 }),
      }),
      `attachmentScan:${dbName}`,
      MIGRATION_BATCH_TIMEOUT_MS,
    );
    if (!page.rows || page.rows.length === 0) break;
    for (const row of page.rows) {
      if (row.doc && row.doc._attachments) found += 1;
    }
    startkey = page.rows[page.rows.length - 1].key;
    if (page.rows.length < MIGRATION_SCAN_BATCH_SIZE) break;
  }

  // now the non-winning leaves, which `allDocs` never returns
  for (const [docId, summary] of leafSummary) {
    if (summary.leafRevs.length < 2) continue;
    for (const rev of summary.leafRevs) {
      // **A failed read is NOT "no attachment" here, and the difference is the whole
      // point of this scan.** Attachment loss through this adapter is silent: the bytes
      // go, replication reports success, and the leaf-revision comparison passes
      // because the revision is identical. So a branch we could not read is a branch we
      // cannot clear, and the failure has to travel — aborting costs nothing, because
      // nothing has been copied and the device stays exactly where it is. Only a
      // genuinely missing leaf is absorbed: the changes feed named it, so it can
      // legitimately have been compacted away between the two reads.
      const leafDoc = await deps.withTimeout(
        readLeafRevisionAsync(db, docId, rev),
        `attachmentScanLeaf:${dbName}`,
        MIGRATION_BATCH_TIMEOUT_MS,
      );
      if (leafDoc && leafDoc._attachments) found += 1;
    }
  }

  return found;
}

/** Read one leaf revision, absorbing only a missing one. See the call site for why
 *  every other failure must travel rather than read as "no attachment". */
async function readLeafRevisionAsync(db: MigrationDb, docId: string, rev: string): Promise<any> {
  try {
    return await db.get(docId, { rev });
  } catch (error) {
    if (isNotFoundError(error)) return undefined;
    throw error;
  }
}

/**
 * Copy the `_local/` documents replication will not carry. (R5b)
 *
 * **This is the requirement most easily missed, and missing it loses user data
 * silently.** PouchDB replication never transfers `_local/*`, and `changes()` never
 * reports them — so a copy that relies on replication alone drops them AND R6's
 * verification passes anyway.
 *
 * They are not all housekeeping. This app keeps genuine, device-only user data there:
 * `_local/conflict-archive-index` and the archived losing branches of auto-resolved
 * sync conflicts, which exist nowhere else — not on the device, not on the server —
 * and the scanner preferences the meal-photo and recipe pages keep there deliberately
 * BECAUSE `_local/` does not replicate.
 *
 * **The source's `_rev` is never written.** A source `_local/` document carries a
 * revision such as `0-42`, and these revisions are locally generated counters, so the
 * same document is a different number on each side even on a perfectly correct copy.
 * What is written depends on whether the target already holds the document: nothing at
 * all when it does not (the adapter's create branch), and the TARGET's own revision
 * when it does (the update branch). Get either wrong and the write matches no row and
 * is rejected as a conflict. It fails closed, so nothing is lost — but the migration
 * does not work until somebody unpicks it.
 *
 * **Since #5449 the target may already hold them**, because a background copy keeps its
 * target across boots rather than deleting it before every attempt. That makes both the
 * revision question above and the deletion sweep below reachable on ordinary devices,
 * where before #5449 the target was always empty.
 */
async function copyLocalDocumentsAsync(
  source: MigrationDb,
  target: MigrationDb,
  deps: SqliteMigrationDeps,
  dbName: string,
): Promise<{ source: Map<string, any>; target: Map<string, any> }> {
  const sourceDocs = await readLocalDocumentsAsync(source, deps, dbName);
  // Read the target's own `_local/` documents BEFORE writing any of them. Two things
  // below need to know what was already there, and after the writes it is too late to
  // ask: the revision each write has to quote, and which documents the source has
  // since dropped. Reading the target first is also what finds an archived branch
  // orphaned by an index the source has already replaced (#5449).
  const existingDocs = await readLocalDocumentsAsync(target, deps, dbName);

  for (const [docId, doc] of sourceDocs) {
    const { _rev, ...withoutRev } = doc;
    // #5449: the target can already hold this document, because a background copy
    // keeps its target across boots instead of deleting it before every attempt.
    // Which revision to quote is therefore not a constant:
    //
    //  - target does NOT have it: write with no `_rev` at all. That takes the
    //    adapter's create branch, which is the only one that matches no row.
    //  - target HAS it: quote the TARGET's revision, never the source's. `_local/`
    //    revisions are locally generated counters, so the same document is `0-42` in
    //    the source and `0-3` in the target on a perfectly correct copy. Quoting the
    //    source's number matches no row and the write is rejected as a conflict.
    const existingRev: string | undefined = existingDocs.get(docId)?._rev;
    const toWrite = existingRev === undefined
      ? withoutRev
      : { ...withoutRev, _rev: existingRev };
    await deps.withTimeout(
      target.put(toWrite),
      `localDocPut:${dbName}`,
      MIGRATION_BATCH_TIMEOUT_MS,
    );
  }

  // #5449: and remove what the source no longer has. Replication carries a deletion
  // across as a tombstone, but `_local/` documents do not replicate at all — they are
  // copied by hand, so nothing else would ever drop one. On a kept target that leaves
  // a document the source deleted: the verification below reports it as `extra`, and
  // the migration then fails on that device on every later attempt, permanently.
  //
  // The only ids this can reach are the handful `readLocalDocumentsAsync` looks for —
  // the known ids plus the branches the target's own conflict-archive index names — so
  // it can never touch the view-heal stamp or a replication checkpoint.
  for (const [docId, doc] of existingDocs) {
    if (sourceDocs.has(docId)) continue;
    await deps.withTimeout(
      target.put({ _id: docId, _rev: doc._rev, _deleted: true }),
      `localDocRemove:${dbName}`,
      MIGRATION_BATCH_TIMEOUT_MS,
    );
  }

  const targetDocs = await readLocalDocumentsAsync(target, deps, dbName);
  return { source: sourceDocs, target: targetDocs };
}

/**
 * Read the `_local/` documents worth copying.
 *
 * `allDocs` cannot list them, so they are found by walking the conflict archive's own
 * index plus a list of known ids. The ones deliberately skipped, and why, are in
 * `isLocalDocDeliberatelyNotCopied`.
 */
async function readLocalDocumentsAsync(
  db: MigrationDb,
  deps: SqliteMigrationDeps,
  dbName: string,
): Promise<Map<string, any>> {
  const found = new Map<string, any>();

  async function tryReadAsync(docId: string): Promise<any> {
    try {
      return await deps.withTimeout(
        db.get(docId), `localDocGet:${dbName}`, MIGRATION_BATCH_TIMEOUT_MS);
    } catch {
      // a `_local/` document that is simply not there is the normal case, not a
      // failure — most databases have no conflict archive at all
      return undefined;
    }
  }

  for (const docId of KNOWN_LOCAL_DOC_IDS) {
    const doc = await tryReadAsync(docId);
    if (doc && !isLocalDocDeliberatelyNotCopied(docId, doc)) found.set(docId, doc);
  }

  // walk the conflict archive index for the archived losing branches it names
  const index = found.get(CONFLICT_ARCHIVE_INDEX_ID);
  for (const entryId of readArchiveEntryIds(index)) {
    const doc = await tryReadAsync(entryId);
    if (doc) found.set(entryId, doc);
  }

  return found;
}

/** The conflict archive's index document, which names every archived losing branch. */
const CONFLICT_ARCHIVE_INDEX_ID = '_local/conflict-archive-index';

/** `_local/` ids this app writes and this migration must carry. */
const KNOWN_LOCAL_DOC_IDS: readonly string[] = [
  CONFLICT_ARCHIVE_INDEX_ID,
  '_local/meal-scanner-prefs',
  '_local/recipe-scanner-prefs',
];

/** The archived-branch ids named by the conflict archive index, whatever shape it is in.
 *
 *  Written defensively on purpose: this document was written by whichever version of
 *  the app the user last ran, and a damaged or older-shaped one must be read for what
 *  it does hold rather than throwing on the boot path. */
export function readArchiveEntryIds(index: any): string[] {
  if (!index || typeof index !== 'object') return [];
  const entries = index.entries;
  if (!Array.isArray(entries)) return [];
  const ids: string[] = [];
  for (const entry of entries) {
    if (typeof entry === 'string') ids.push(entry);
    else if (entry && typeof entry === 'object' && typeof entry.id === 'string') ids.push(entry.id);
  }
  return ids;
}

/**
 * Stamp the global view-rebuild version into a migrated database. (R12b)
 *
 * Phase 2 decided that a device which has never run the iOS adapter is exempt from the
 * one-time global view rebuild, and left the implementation here because this is the
 * phase that writes the engine marker.
 *
 * Without it, every migrated Android device bumps every view's heal salt, rebuilds
 * every view index from scratch on the most expensive boot of its life, and files a
 * problem-tier report — all to repair damage from a bug it never ran, and all landing
 * in the middle of phase 4's ramp where it reads as an incident.
 *
 * The stamp is PER DATABASE, because `globalRebuildVersion` lives in each database's
 * own `_local/view-heal`. R7's "never per-database" governs the engine MARKER, not
 * this.
 */
async function stampViewRebuildVersionAsync(
  target: MigrationDb,
  deps: SqliteMigrationDeps,
  dbName: string,
): Promise<void> {
  let doc: any;
  try {
    doc = await deps.withTimeout(
      target.get(VIEW_HEAL_DOC_ID), `viewHealGet:${dbName}`, MIGRATION_BATCH_TIMEOUT_MS);
  } catch {
    // not there is the normal case — the view-heal document is deliberately NOT
    // copied, so a freshly migrated database has none
    doc = { _id: VIEW_HEAL_DOC_ID };
  }
  doc.globalRebuildVersion = GLOBAL_VIEW_REBUILD_VERSION;
  await deps.withTimeout(
    target.put(doc), `viewHealPut:${dbName}`, MIGRATION_BATCH_TIMEOUT_MS);
}

/**
 * Stop a replication we may have walked away from.
 *
 * The bound helper only stops WAITING — it cannot cancel the underlying operation —
 * so an abandoned replication would keep writing into the SQLite target and keep
 * occupying the single native bridge for the rest of the boot.
 *
 * A handle that has already finished throws when cancelled, and that is the GOOD
 * outcome: there is nothing left to stop. The failure that matters is the one that
 * brought us here, and the caller returns it as the migration's outcome.
 */
function cancelReplicationQuietly(handle: any): void {
  try {
    handle?.cancel?.();
  } catch (cancelError) {
    // nothing to stop, which is what we wanted. Deliberately not reported: it would
    // replace the real failure the caller is on its way to report.
  }
}

/**
 * Listen to a replication's progress, and return a function that stops listening.
 *
 * **`checkpoint` is the signal that matters, and `change` alone is not enough.**
 * PouchDB emits `change` only for a batch that actually WROTE something, so a copy
 * that is reading and comparing — which is most of what a copy does when the target
 * already holds some of the documents — looks silent on `change` while working
 * perfectly. `checkpoint` fires throughout: per change read from the source, per
 * document compared, per batch started, and after each checkpoint is written. Both are
 * subscribed because either one arriving proves the same thing: the copy is alive.
 *
 * The returned function has to be safe to call twice and safe to call late — PouchDB
 * removes every listener of its own when a replication ends, so by the time the bound
 * cleans up there may be nothing left to remove.
 */
function subscribeToReplicationProgress(handle: any, onProgress: () => void): () => void {
  handle?.on?.('checkpoint', onProgress);
  handle?.on?.('change', onProgress);
  return () => {
    handle?.removeListener?.('checkpoint', onProgress);
    handle?.removeListener?.('change', onProgress);
  };
}

/**
 * Close a database handle without letting the close itself change the outcome.
 *
 * Called from a `finally`, after the outcome has been decided. A handle that refuses
 * to close costs this page load one open database, which the page load ends anyway.
 * Reporting it here would replace the REAL outcome — the copy failure or success on
 * its way out — with a tidy-up detail. The close is bounded all the same: a tidy-up
 * that never returns would hold the whole boot.
 */
async function closeQuietlyAsync(db: MigrationDb, deps: SqliteMigrationDeps): Promise<void> {
  try {
    // bounded (#5448) for the same reason every other call here is: a close that never
    // comes back would hold whatever is waiting on it, with nothing said. A bound that
    // runs out lands in the same catch as a close that failed, which is where a tidy-up
    // belongs.
    await deps.withTimeout(db.close(), 'closeDatabase', MIGRATION_BATCH_TIMEOUT_MS);
  } catch (closeError) {
    // the outcome the caller is returning is what gets reported, not this
  }
}

/**
 * The direction this copy runs in, filling in the forward one when nothing said.
 *
 * The forward direction is the default rather than something every caller states, so
 * phase 3's callers and tests are untouched by phase 4 and the diff that added the
 * reverse direction cannot have changed the forward one by accident.
 */
function resolveCopyDirection(deps: SqliteMigrationDeps): CopyDirection {
  return deps.direction ?? {
    sourceAdapter: IDB_ADAPTER,
    targetAdapter: SQLITE_ADAPTER,
    // forward, the copy writes the same logical name on the other engine
    targetNameFor: (sourceLogicalName: string) => sourceLogicalName,
    // the native plugin is addressed by the STORAGE name, prefix and all
    deleteTargetDatabaseAsync: (targetLogicalName: string) =>
      deps.deleteSqliteDatabaseAsync(`_pouch_${targetLogicalName}`),
    recordTargetName: (targetLogicalName: string) =>
      recordTargetNameForSignOut(deps, targetLogicalName),
  };
}

/** What one database's copy produced. */
interface CopyOneResult {
  outcome: MigrationOutcome;
  documentsCopied: number;
  /** filled in on a verification mismatch, for the report */
  verification?: VerificationResult;
  /** filled in on an attachment abort */
  attachmentCount?: number;
  error?: unknown;
}

/**
 * Copy ONE database, verify it, and say what happened. (R5, R5b, R6, R12b)
 *
 * Closes the SQLite target whatever the outcome, leaves the IndexedDB source OPEN (the
 * `finally` says why), and never throws: the caller has to be able to tell a bound from
 * a mismatch from an attachment abort, and each is a different report and a different
 * repair.
 */
async function copyOneDatabaseAsync(
  logicalName: string,
  deps: SqliteMigrationDeps,
): Promise<CopyOneResult> {
  const direction = resolveCopyDirection(deps);
  const targetName = direction.targetNameFor(logicalName);
  const source = deps.openDatabase(logicalName, direction.sourceAdapter);
  const target = deps.openDatabase(targetName, direction.targetAdapter);

  try {
    const targetInfo = await deps.withTimeout(
      target.info(), `targetInfo:${logicalName}`, MIGRATION_BATCH_TIMEOUT_MS);
    const state = await readTargetStateAsync(target, deps, logicalName);
    const sourceDbId = await identityOfAsync(source, deps, `sourceId:${logicalName}`);
    const targetDbId = await identityOfAsync(target, deps, `targetId:${logicalName}`);
    const targetIsStale = shouldDeleteExistingTarget({
      state,
      targetHasContent: (targetInfo?.doc_count ?? 0) > 0,
      sourceDbId,
      targetDbId,
      appVersion: deps.appVersion,
    });

    if (deps.phase === MigrationPhase.Switch) {
      // #5449: the switch half FINISHES a copy the background half has already made
      // and verified. When there is no such copy — the background half is part-way
      // through, or this device has never run it, or what it left can no longer be
      // trusted — there is nothing to finish. Pulling a whole database across on the
      // boot screen is the very thing this change exists to stop, so it does nothing
      // and leaves. Nothing is spent and nothing is reported: a device part-way
      // through a copy that legitimately takes several sessions is not a problem.
      if (!state?.bulkCopyVerified || targetIsStale) {
        return { outcome: MigrationOutcome.BulkCopyNotReady, documentsCopied: 0 };
      }
      return await finishAndVerifyAsync(source, target, deps, logicalName, state);
    }

    // ── the background half ──
    //
    // #5449: a partially filled target is KEPT, and the copy resumes from PouchDB's own
    // checkpoint instead of being deleted and restarted from zero. That is what makes a
    // copy measured in minutes survivable: a device killed part-way through picks up
    // where it stopped, rather than doing the same doomed five minutes on every launch.
    // When a kept target can no longer be trusted — and why each of those rules exists —
    // is in `shouldDeleteExistingTarget`.
    if (targetIsStale) {
      await closeQuietlyAsync(target, deps);
      await deps.withTimeout(
        direction.deleteTargetDatabaseAsync(targetName),
        `deleteTarget:${targetName}`,
        MIGRATION_BATCH_TIMEOUT_MS,
      );
      // Copying into a database this handle no longer describes is not something to
      // attempt here. The caller re-enters on a fresh handle, which is one extra pass
      // of a background task and no cost to anybody.
      return { outcome: MigrationOutcome.TargetRestarted, documentsCopied: 0 };
    }

    // read the source's leaf summary FIRST: the attachment scan needs it to know which
    // documents have non-winning leaves worth fetching
    const sourceSummary = await summariseChangesAsync(source, deps, `sourceChanges:${logicalName}`);

    const attachmentCount = await findAttachmentsAsync(source, deps, logicalName, sourceSummary);
    if (attachmentCount > 0) {
      return { outcome: MigrationOutcome.AttachmentsFound, documentsCopied: 0, attachmentCount };
    }

    const copied = await replicateInSlicesAsync(source, target, deps, logicalName);

    // Verify the bulk copy in FULL, here in the background, where its cost lands on
    // nobody. This is the comparison the switch half is then allowed to narrow: it
    // re-checks only what changed after the sequence recorded below, and that is sound
    // only because the whole database really was compared once, here (#5449).
    const verification = await verifyBulkCopyAsync(
      source, target, deps, logicalName, sourceSummary);
    if (!verification.matched) {
      return { outcome: MigrationOutcome.VerificationMismatch, documentsCopied: 0, verification };
    }

    await writeTargetStateAsync(target, deps, logicalName, {
      bulkCopyVerified: true,
      // The sequence the comparison above covered. Taken BEFORE the copy began, so
      // anything written while it ran is simply newer than this number and falls to the
      // switch half — which is the correct place for it. A number taken afterwards
      // would silently exclude exactly the documents most at risk.
      verifiedThroughSeq: copied.sourceSeqAtStart,
      sourceDbId,
      targetDbId,
      appVersion: deps.appVersion,
    });

    return { outcome: MigrationOutcome.Completed, documentsCopied: copied.documentsCopied };
  } catch (error) {
    // #5449: the app asked this copy to stop, because it is tearing down. Nothing is
    // wrong, nothing was lost — the target keeps what it has and the copy resumes from
    // its checkpoint on a later page load — so this spends no budget and reports
    // nothing. Every replication cancels itself where it was started (`runOneSliceAsync`
    // and `finishAndVerifyAsync` each own their handle), which is why there is no
    // module-level handle to cancel here any more.
    if (error instanceof MigrationCancelledError) {
      return { outcome: MigrationOutcome.Cancelled, documentsCopied: 0 };
    }
    // Name the outcome for what actually happened. Everything in this block used to be
    // reported as `BoundHit`, whose own meaning is "a read or replication batch
    // exceeded its bound" — so a rejected `_local/` write or view-heal stamp was filed
    // as a timeout. The underlying error travels either way, but the problem NAME is
    // what phase 4's ramp counts, and a ramp that cannot tell a slow device from a
    // broken write is counting the wrong thing.
    const outcome = isLocalStoreNotAnsweringError(error)
      ? MigrationOutcome.BoundHit
      : MigrationOutcome.CopyFailed;
    return { outcome, documentsCopied: 0, error };
  } finally {
    // The SOURCE is deliberately NOT closed. PouchDB's IndexedDB adapter keeps ONE
    // browser connection per database name and hands the same one to every handle on
    // that name, so closing this handle closes the connection underneath the running
    // app's handle on the same database. That is what #5448 observed: the image upload
    // outbox failed with "the database connection is closing" half a second after the
    // migration finished, and stayed broken for the rest of the page load.
    //
    // #5449 turns that from careful into MANDATORY. The background copy runs while the
    // app is up, so the app certainly holds handles on these databases — what was a
    // narrow window on the boot screen is now the normal case.
    //
    // Leaving it open costs nothing worth counting — one browser connection until the
    // page load ends — and it does not block phase 4's later deletion, which happens on
    // a different page load, and which the adapter's own version-change handler would
    // make way for anyway. The TARGET is still closed: it is this migration's own
    // handle, and nothing else in the app holds one on the SQLite side.
    await closeQuietlyAsync(target, deps);
  }
}

/**
 * A database's own persisted id. (#5449)
 *
 * The id is what tells a REBUILT database from the one a recorded copy was actually
 * made of, and the case that matters is a user signing out and back in: that destroys
 * the source and recreates it under the same name. Replication only ever ADDS, so
 * branches the old source held and the new one does not would sit in the target for
 * ever, failing verification as `extra` on every later attempt.
 *
 * **Deliberately not `info()`.** That reports `db_name` — the name, which is identical
 * before and after the rebuild, so an identity built on it would never notice.
 *
 * An id that cannot be read answers the empty string, which never matches a recorded
 * one, so the target is thrown away and copied again rather than believed. That is the
 * safe direction: it costs one background copy, and the alternative is trusting a
 * database nothing can vouch for.
 */
async function identityOfAsync(
  db: MigrationDb,
  deps: SqliteMigrationDeps,
  operationName: string,
): Promise<string> {
  try {
    const id = await deps.withTimeout(db.id(), operationName, MIGRATION_BATCH_TIMEOUT_MS);
    return typeof id === 'string' ? id : '';
  } catch {
    // see above: unreadable means "cannot vouch for itself", which is not an error
    // here — it is an answer, and it leads to copying again
    return '';
  }
}

/**
 * Replicate in paced slices, pausing between them. (#5449)
 *
 * The copy takes about five minutes on a real account, and it now runs while somebody
 * is using the app. Handing PouchDB the whole job at once would give it five minutes of
 * uninterrupted work on the thread the interface draws on. So it is cut into slices: a
 * few batches, then a pause whose length the app decides (`pauseBetweenSlicesAsync`),
 * then the next slice.
 *
 * **Each slice resumes from PouchDB's own checkpoint, so a slice costs almost nothing
 * to restart** — two small reads and one `info()`, not a re-walk of the source. The
 * checkpoint is written only once a batch's documents are in the target, so a slice cut
 * off mid-batch re-does at most that batch, and re-doing it is idempotent.
 *
 * **Slices are counted in batches WRITTEN, and the reason is a livelock.** PouchDB emits
 * a progress signal per document read or compared, so a slice ended after N progress
 * signals could end having written nothing at all — and a copy that writes nothing per
 * slice never finishes, however many slices it runs. The `change` event fires once per
 * batch that actually wrote, which is the thing worth counting.
 *
 * **A cancelled replication RESOLVES; it does not throw.** Its answer carries
 * `status: 'cancelled'`, and reading that as "the copy finished" would stop after one
 * slice and report a part-filled database as a complete copy.
 */
async function replicateInSlicesAsync(
  source: MigrationDb,
  target: MigrationDb,
  deps: SqliteBackgroundMigrationDeps,
  logicalName: string,
): Promise<{ documentsCopied: number; sourceSeqAtStart: unknown }> {
  const infoAtStart = await deps.withTimeout(
    source.info(), `sourceInfo:${logicalName}`, MIGRATION_BATCH_TIMEOUT_MS);
  const sourceSeqAtStart = infoAtStart?.update_seq ?? 0;

  let documentsCopied = 0;
  for (;;) {
    // #5449: stop before starting more work when the app is tearing down. The copy
    // resumes from its checkpoint on a later page load, so giving up here costs
    // nothing but the slices not yet run.
    if (deps.isCancelled()) throw new MigrationCancelledError();
    const slice = await runOneSliceAsync(source, target, deps, logicalName);
    documentsCopied += slice.documentsCopied;
    if (slice.complete) return { documentsCopied, sourceSeqAtStart };
    await deps.pauseBetweenSlicesAsync();
  }
}

/** Thrown when the app asks an in-flight copy to stop. (#5449)
 *
 *  A named error rather than a flag threaded through every return type: the copy gives
 *  up from wherever it happens to be, and the one place that cares — `copyOneDatabaseAsync`
 *  — turns it into an outcome that spends nothing and reports nothing. */
class MigrationCancelledError extends Error {
  constructor() {
    super('the migration was cancelled');
    this.name = 'MigrationCancelled';
  }
}

/** One slice: replicate until a few batches have been written, then stop. (#5449) */
async function runOneSliceAsync(
  source: MigrationDb,
  target: MigrationDb,
  deps: SqliteBackgroundMigrationDeps,
  logicalName: string,
): Promise<{ complete: boolean; documentsCopied: number }> {
  const handle: any = source.replicate.to(target, {
    batch_size: MIGRATION_BACKGROUND_BATCH_SIZE,
    batches_limit: MIGRATION_BACKGROUND_BATCHES_LIMIT,
  });

  let batchesWritten = 0;
  const stopWhenSliceIsFull = () => {
    batchesWritten += 1;
    if (batchesWritten >= MIGRATION_SLICE_BATCHES) cancelReplicationQuietly(handle);
  };
  handle?.on?.('change', stopWhenSliceIsFull);

  // #5449: let the app stop this replication if it starts tearing down mid-slice.
  // Checking `isCancelled` between slices is not enough on its own — a slice can be
  // several seconds of writing, and the teardown deletes these files.
  const unregisterCancel = deps.registerCancellable(() => cancelReplicationQuietly(handle));

  let result: any;
  try {
    result = await deps.withIdleTimeout<any>(
      handle, `replicate:${logicalName}`, MIGRATION_BATCH_TIMEOUT_MS,
      onProgress => subscribeToReplicationProgress(handle, onProgress));
  } catch (error) {
    // The bound helper only stops WAITING — it cannot cancel the underlying operation.
    // An abandoned replication carries on writing into the SQLite target and keeps
    // occupying the single native bridge for the rest of the page load, which on a
    // background copy means for as long as the app is open. The handle is local to this
    // slice, so this is the only place that can stop it.
    cancelReplicationQuietly(handle);
    throw error;
  } finally {
    handle?.removeListener?.('change', stopWhenSliceIsFull);
    unregisterCancel();
  }

  return {
    // a replication we stopped ourselves answers `cancelled`; anything else means it
    // ran out of work, which is the only thing that ends the copy
    complete: result?.status !== 'cancelled',
    documentsCopied: result?.docs_written ?? 0,
  };
}

/**
 * Finish a bulk copy the background half has already verified, and check it. (#5449)
 *
 * This is the whole of what now happens on the boot screen. Everything expensive has
 * already been done, so what is left is whatever the source changed after
 * `verifiedThroughSeq` — one session's writes at most.
 *
 * **The verification is narrowed to match, and that is sound rather than merely cheap.**
 * It compares leaf revisions for only the documents that changed after that sequence,
 * which are exactly the ones the earlier full comparison could not have covered. The
 * unchanged remainder was compared document for document, in the background. Narrowing
 * it without that having happened would pass a copy that had quietly dropped a tombstone
 * or a conflicting branch, which is the one thing this verification exists to catch.
 */
async function finishAndVerifyAsync(
  source: MigrationDb,
  target: MigrationDb,
  deps: SqliteMigrationDeps,
  logicalName: string,
  state: MigrationTargetState,
): Promise<CopyOneResult> {
  const replicationHandle: any = source.replicate.to(
    target, { batch_size: MIGRATION_SCAN_BATCH_SIZE });
  let replicationResult: { docs_written?: number };
  try {
    replicationResult = await deps.withIdleTimeout<{ docs_written?: number }>(
      replicationHandle, `replicate:${logicalName}`, MIGRATION_BATCH_TIMEOUT_MS,
      onProgress => subscribeToReplicationProgress(replicationHandle, onProgress));
  } catch (error) {
    // The bound helper only stops WAITING — it cannot cancel the underlying operation,
    // so an abandoned replication carries on writing and holds the single native bridge
    // for the rest of this page load. That matters more here than anywhere else in this
    // module: this runs on the boot screen, on the page load that is about to decide
    // which engine the device uses. `runOneSliceAsync` guards the same way.
    cancelReplicationQuietly(replicationHandle);
    throw error;
  }

  // what moved since the full comparison — asked of the SOURCE, which is the side that
  // decides what still needs checking
  const changedSummary = await summariseChangesAsync(
    source, deps, `sourceChanges:${logicalName}`, state.verifiedThroughSeq);
  const changedIds = [...changedSummary.keys()];

  if (changedIds.length > 0) {
    // An attachment can have arrived since the background scan, and this adapter loses
    // those bytes SILENTLY: a Blob serialises to an empty object, replication reports
    // success, and the leaf comparison passes because the revision is identical.
    const attachmentCount = await findAttachmentsAsync(
      source, deps, logicalName, changedSummary);
    if (attachmentCount > 0) {
      return { outcome: MigrationOutcome.AttachmentsFound, documentsCopied: 0, attachmentCount };
    }

    const targetSummary = await summariseChangedIdsAsync(
      target, deps, `targetChanges:${logicalName}`, changedIds);
    const verification = verifyDatabaseContents(changedSummary, targetSummary);
    if (!verification.matched) {
      return { outcome: MigrationOutcome.VerificationMismatch, documentsCopied: 0, verification };
    }
  }

  const localDocs = await copyLocalDocumentsAsync(source, target, deps, logicalName);
  const localVerification = verifyLocalDocuments(localDocs.source, localDocs.target);
  if (!localVerification.matched) {
    return {
      outcome: MigrationOutcome.VerificationMismatch,
      documentsCopied: 0,
      verification: localVerification,
    };
  }

  // A belt, one read of each side: the live-document counts must agree. It is blind to
  // a lost conflict branch — which is what the leaf comparisons above are for — but it
  // is the one cheap check that catches a target wiped or replaced wholesale between
  // the background verification and now.
  const sourceInfo = await deps.withTimeout(
    source.info(), `sourceInfo:${logicalName}`, MIGRATION_BATCH_TIMEOUT_MS);
  const targetInfo = await deps.withTimeout(
    target.info(), `targetInfo:${logicalName}`, MIGRATION_BATCH_TIMEOUT_MS);
  const sourceCount = sourceInfo?.doc_count;
  const targetCount = targetInfo?.doc_count;
  if (typeof sourceCount !== 'number' || typeof targetCount !== 'number'
    || sourceCount !== targetCount) {
    return {
      outcome: MigrationOutcome.VerificationMismatch,
      documentsCopied: 0,
      verification: {
        matched: false,
        sourceCount: typeof sourceCount === 'number' ? sourceCount : 0,
        targetCount: typeof targetCount === 'number' ? targetCount : 0,
        differenceCount: 1,
        sampleIds: [],
        // never a document id here: this check knows only the totals
        sampleReasons: ['doc_count'],
      },
    };
  }

  await stampViewRebuildVersionAsync(target, deps, logicalName);

  return {
    outcome: MigrationOutcome.Completed,
    documentsCopied: replicationResult?.docs_written ?? 0,
  };
}

/**
 * Compare the whole copy, and give a moving source a few chances to settle. (#5449)
 *
 * **Why this is not one comparison.** The source is LIVE for the whole of the bulk copy
 * — that is what running it in the background means — and the copy takes minutes. So the
 * snapshot the verification started from is, by the time the copy finishes, simply out of
 * date: a workout logged during those minutes makes its document differ, and a single
 * strict comparison would call that a verification mismatch. On a real device it would do
 * so nearly every time, turning a working copy into a reported failure that spends an
 * attempt.
 *
 * So each round carries across whatever moved, then re-checks ONLY the documents that
 * still differ, reading both sides fresh. A document that differed because it changed
 * mid-copy agrees on the next look. One that differs because the copy really did lose
 * something differs however many times it is asked, and that is what this must still
 * catch — so the rounds are few, and running out of them is a mismatch, not a pass.
 */
async function verifyBulkCopyAsync(
  source: MigrationDb,
  target: MigrationDb,
  deps: SqliteBackgroundMigrationDeps,
  logicalName: string,
  sourceSummaryAtStart: Map<string, DocumentLeafSummary>,
): Promise<VerificationResult> {
  let sourceSummary = sourceSummaryAtStart;
  let targetSummary = await summariseChangesAsync(
    target, deps, `targetChanges:${logicalName}`);
  let verification = verifyDatabaseContents(sourceSummary, targetSummary);

  for (let round = 0; !verification.matched && round < MIGRATION_VERIFY_RECONCILE_ROUNDS; round++) {
    const unsettledIds = idsThatDiffer(sourceSummary, targetSummary);
    if (unsettledIds.length === 0) break;

    // carry across whatever the source has done since, then look at just those
    // documents again — as they BOTH are now, rather than against a stale snapshot
    await replicateInSlicesAsync(source, target, deps, logicalName);
    sourceSummary = await summariseChangedIdsAsync(
      source, deps, `sourceChanges:${logicalName}`, unsettledIds);
    targetSummary = await summariseChangedIdsAsync(
      target, deps, `targetChanges:${logicalName}`, unsettledIds);

    // **Two empty summaries compare EQUAL, and that must not read as a pass here.**
    // Every id in `unsettledIds` is one the source has just been asked about because
    // the two sides disagreed about it. If the source now reports nothing at all for
    // any of them, the read did not answer — it did not tell us they are gone — and
    // believing it would turn a copy that lost documents into a verified one. This is
    // the same trap the changes-feed reads guard against with `return_docs: false`.
    if (sourceSummary.size === 0) return verification;

    verification = verifyDatabaseContents(sourceSummary, targetSummary);
  }

  return verification;
}

/**
 * Which document ids do these two summaries disagree about? (#5449)
 *
 * `verifyDatabaseContents` answers WHETHER they match and keeps a bounded sample of ids
 * for the report — bounded because a report must never carry a whole database's worth of
 * ids. The reconcile above needs the complete list, which is a different question, so it
 * is asked separately rather than by widening that sample.
 */
function idsThatDiffer(
  source: Map<string, DocumentLeafSummary>,
  target: Map<string, DocumentLeafSummary>,
): string[] {
  const ids: string[] = [];
  for (const [docId, sourceDoc] of source) {
    const targetDoc = target.get(docId);
    if (!targetDoc
      || targetDoc.deleted !== sourceDoc.deleted
      || targetDoc.leafRevs.join(',') !== sourceDoc.leafRevs.join(',')) {
      ids.push(docId);
    }
  }
  for (const docId of target.keys()) {
    if (!source.has(docId)) ids.push(docId);
  }
  return ids;
}

/** Read a target's own record of what has been copied into it. (#5449) */
async function readTargetStateAsync(
  target: MigrationDb,
  deps: SqliteMigrationDeps,
  dbName: string,
): Promise<MigrationTargetState | undefined> {
  try {
    const doc = await deps.withTimeout(
      target.get(MIGRATION_STATE_DOC_ID), `stateGet:${dbName}`, MIGRATION_BATCH_TIMEOUT_MS);
    return readMigrationTargetState(doc);
  } catch {
    // No record is the ordinary case on a target nothing has copied into yet, and an
    // unreadable one means the same thing here: this target cannot vouch for itself.
    // Both lead to copying again, which is the safe direction.
    return undefined;
  }
}

/** Record what has been copied into this target, inside the target itself. (#5449) */
async function writeTargetStateAsync(
  target: MigrationDb,
  deps: SqliteMigrationDeps,
  dbName: string,
  state: MigrationTargetState,
): Promise<void> {
  let existingRev: string | undefined;
  try {
    const existing = await deps.withTimeout(
      target.get(MIGRATION_STATE_DOC_ID), `stateGet:${dbName}`, MIGRATION_BATCH_TIMEOUT_MS);
    existingRev = existing?._rev;
  } catch {
    // not there yet, which is the ordinary first time
  }
  const doc: any = { _id: MIGRATION_STATE_DOC_ID, ...state };
  // `_local/` revisions are locally generated counters, so the target's own revision is
  // the only one that can be quoted here — the same rule `copyLocalDocumentsAsync` obeys
  if (existingRev) doc._rev = existingRev;
  await deps.withTimeout(
    target.put(doc), `statePut:${dbName}`, MIGRATION_BATCH_TIMEOUT_MS);
}

/**
 * Leaf summaries for a NAMED SET of documents only. (#5449)
 *
 * `doc_ids` with the `_doc_ids` filter is honoured by both adapters and still reports
 * EVERY leaf of each document, which is the property the comparison depends on: a
 * winner-only answer would pass a copy that had dropped a conflicting branch.
 */
async function summariseChangedIdsAsync(
  db: MigrationDb,
  deps: SqliteMigrationDeps,
  operationName: string,
  docIds: string[],
): Promise<Map<string, DocumentLeafSummary>> {
  const summary = new Map<string, DocumentLeafSummary>();
  const feed = db.changes({
    since: 0,
    style: 'all_docs',
    return_docs: false,
    doc_ids: docIds,
    filter: '_doc_ids',
  });
  feed.on('change', (change: any) => {
    summary.set(change.id, {
      leafRevs: change.changes.map((leaf: any) => leaf.rev).sort(),
      deleted: change.deleted === true,
    });
  });
  await deps.withTimeout(feed, operationName, MIGRATION_BATCH_TIMEOUT_MS);
  return summary;
}

/**
 * Re-read both rollout controls and ask again whether to migrate. (R2)
 *
 * This is what makes turning a control back OFF take effect immediately rather than
 * one boot later — including taking a named person off the allow-list, which is the
 * control an operator reaches for in a hurry.
 *
 * **The database name is the trap.** The common database's local PouchDB name is
 * `global_common-fr_<uid>`, and it is rewritten again if this device has been through
 * a #4401 storage reset. Opening `new PouchDB('global_common')` would CREATE a
 * brand-new empty database, read no config, resolve to "no", clear the request marker
 * — so the migration would silently never happen on any device — and leave a stray
 * `_pouch_global_common` behind that the next attempt's enumeration would try to
 * migrate. The name is built through `currentLocalStoreName` and required to be in the
 * enumeration before it is opened.
 */
export async function reReadControlsAsync(
  commonDbLogicalName: string,
  deps: SqliteMigrationDeps,
): Promise<{ outcome: MigrationOutcome; error?: unknown } | { migrate: boolean }> {
  // never closed on the way out — see the note at the end of this function
  // the SOURCE engine, not `idb` outright: the config lives wherever this device's data
  // currently lives, which for a rollback is SQLite (#5270)
  const commonDb = deps.openDatabase(commonDbLogicalName, resolveCopyDirection(deps).sourceAdapter);
  try {
    // **Only a NOT-FOUND is absorbed here.** A document that is simply absent means
    // "no value configured", which resolves to "do not migrate" — the correct answer.
    // Any OTHER failure is the store not answering, which is a completely different
    // thing and must surface as `ConfigReadFailed`: it is the outcome the retry rules
    // are built around, and swallowing it would resolve to "the config says no",
    // silently clear the request marker, reset the abort budget, and leave a device
    // whose common database errors on every read re-requesting for ever with no
    // telemetry and no budget ever spent.
    const docIds = deps.controlDocIds
      ?? { allowList: MIGRATION_ALLOW_LIST_DOC_ID, percent: MIGRATION_PERCENT_DOC_ID };
    const allowListDoc = await deps.withTimeout(
      readConfigDocAsync(commonDb, docIds.allowList),
      'configRead:allowList',
      MIGRATION_CONFIG_READ_TIMEOUT_MS,
    );
    const percentDoc = await deps.withTimeout(
      readConfigDocAsync(commonDb, docIds.percent),
      'configRead:percent',
      MIGRATION_CONFIG_READ_TIMEOUT_MS,
    );

    // resolved through the SHARED resolver, so this cannot drift from how
    // `ConfigDbService` resolves the very same documents
    const allowListValue = resolveConfigRecordForUser(allowListDoc, deps.uid);
    const percentValue = resolveConfigRecordForUser(percentDoc, deps.uid);

    return { migrate: deps.decideFromControls(allowListValue, percentValue) };
  } catch (error) {
    // same reasoning as the enumeration: this abort counts towards giving up on the
    // device permanently, so the reason has to reach a future debugger
    return { outcome: MigrationOutcome.ConfigReadFailed, error };
  }
  // The handle is deliberately left OPEN on the way out of every branch above, for the
  // reason `copyOneDatabaseAsync` spells out: PouchDB's IndexedDB adapter shares one
  // browser connection per database name, so closing this handle would also close the
  // running app's connection to the common database (#5448).
}

/**
 * Read one config document, treating "not there" as no value and letting every other
 * failure through.
 *
 * `isPouchNotFound` cannot be used: it lives in `@cf-shared/data-model/pouch`, and this
 * module is loaded by a node test where that import chain is not available. The two
 * fields it tests are checked inline instead, which is the same shape the E2E helpers
 * use for the same reason.
 */
async function readConfigDocAsync(db: MigrationDb, docId: string): Promise<any> {
  try {
    return await db.get(docId);
  } catch (error) {
    if (isNotFoundError(error)) return undefined;
    // not a missing document — the store failed to answer. Let it travel.
    throw error;
  }
}

/** Is this the "no such document" rejection, as opposed to a real read failure? */
function isNotFoundError(error: any): boolean {
  return error?.status === 404 || error?.name === 'not_found';
}

/** What `runSqliteMigrationAsync` needs to know about the databases on this device. */
interface EnumerationResult {
  outcome?: MigrationOutcome;
  /** why the enumeration failed, when it did — travels into the report */
  error?: unknown;
  logicalNames?: string[];
  commonDbName?: string;
}

/**
 * Find the databases to copy, and refuse if the answer cannot be trusted. (R4)
 *
 * **An empty or failed enumeration is a FAILURE, not a success.** R7 writes the engine
 * marker "after every database has been copied and verified", which is vacuously true
 * of zero databases — so without this rule a failed `indexedDB.databases()` call would
 * flip the device to SQLite and open brand-new empty databases. Worse, the
 * `db_lastSynced_utc_*` flags are keyed on the UNCHANGED database name, so such a
 * device could take the "recently synced" fast path and never even re-download.
 */
async function enumerateAsync(deps: SqliteMigrationDeps): Promise<EnumerationResult> {
  let indexedDbNames: string[];
  try {
    // bounded like every other call on this path (#5448). `indexedDB.databases()` is the
    // same API family whose silent non-answer `local-store-timeout.ts` exists for, and
    // since the splash watchdog is switched off for this whole stage, an unbounded wait
    // here would be a splash that never changes and never reports anything.
    indexedDbNames = await deps.withTimeout(
      listPouchIndexedDbNamesAsync(deps.indexedDb, { excludeViewIndexes: true }),
      'enumerateIndexedDb',
      MIGRATION_BATCH_TIMEOUT_MS,
    );
  } catch (error) {
    // carry WHY forward rather than only THAT it failed: this abort spends the
    // consecutive-abort budget and eventually stops the device migrating for good,
    // so a debugger needs the underlying reason, not just the outcome name
    return { outcome: MigrationOutcome.EnumerationFailed, error };
  }

  const logicalNames = indexedDbNames
    .map(toLogicalDbName)
    // skip another account's databases, and the abandoned pre-reset generation of a
    // renamed one — dead data by definition, and on the devices this ticket family is
    // about it is precisely the database that stopped answering, so copying it would
    // burn the whole migration window and then trip the bound
    .filter(name => !belongsToAnotherAccount(name, deps.uid, deps.managedClientUid))
    .filter(name => name === deps.currentLocalStoreName(baseNameOf(name)));

  // the user's OWN database must be there. Either shape is accepted, because a coach
  // signed in while managing a client opens `userdb-coach_<clientUid>` rather than
  // their own `userdb-fr_<uid>`.
  const ownName = deps.currentLocalStoreName(localUserDbBaseName(deps.uid));
  const coachName = deps.managedClientUid
    ? deps.currentLocalStoreName(localCoachClientDbBaseName(deps.managedClientUid))
    : undefined;
  const hasOwn = logicalNames.includes(ownName) || (!!coachName && logicalNames.includes(coachName));
  if (!hasOwn) return { outcome: MigrationOutcome.OwnDatabaseMissing };

  // and so must the common database, or R2's kill switch cannot be consulted. Do NOT
  // "recover" by opening it anyway — that is the brand-new-empty-database trap.
  const commonDbName = deps.currentLocalStoreName(`global_common-fr_${deps.uid}`);
  if (!logicalNames.includes(commonDbName)) {
    return { outcome: MigrationOutcome.CommonDatabaseMissing };
  }

  return { logicalNames, commonDbName };
}

/** Strip a #4401 reset generation suffix, giving the base name a generation applies to.
 *
 *  Delegates to the module that OWNS the suffix format rather than repeating the
 *  pattern — the repetition is what got the spelling wrong before. */
export function baseNameOf(logicalName: string): string {
  return stripLocalStoreGeneration(logicalName);
}

/**
 * Run the migration. (R1's migrating boot, R2, R4-R7, R12b)
 *
 * Never throws: it is called on the boot path, and a throw here would surface as an
 * unhandled rejection in the middle of a boot. Every failure comes back as an
 * {@link MigrationOutcome} the caller turns into a report and a budget decision.
 *
 * The engine marker is NOT written here — the caller writes it, once, after this
 * returns {@link MigrationOutcome.Completed}. That keeps "the marker moves only after
 * everything is verified" in one place rather than spread across this function's
 * early returns.
 */
export async function runSqliteMigrationAsync(
  deps: SqliteMigrationDeps,
): Promise<MigrationResult> {
  const enumeration = await enumerateAsync(deps);
  if (enumeration.outcome) {
    return {
      outcome: enumeration.outcome, error: enumeration.error,
      databasesCopied: 0, documentsCopied: 0,
    };
  }

  const controls = await reReadControlsAsync(enumeration.commonDbName!, deps);
  if ('outcome' in controls) {
    return {
      outcome: controls.outcome, error: controls.error,
      databasesCopied: 0, documentsCopied: 0,
    };
  }
  if (!controls.migrate) {
    return { outcome: MigrationOutcome.ConfigSaysNo, databasesCopied: 0, documentsCopied: 0 };
  }

  const copied = await copyDatabasesAsync(enumeration.logicalNames!, deps);
  if (copied.outcome !== MigrationOutcome.Completed) {
    // #5449: neither of these is a failure. `BulkCopyNotReady` is the ordinary state of a
    // device part-way through a copy that legitimately takes several sessions, and the
    // switch half hits it on every page load until the background half finishes.
    // `Cancelled` is the app tearing down. Reporting either would file a problem per
    // launch per database on every migrating device — exactly the shape phase 4's ramp is
    // supposed to read as an incident.
    if (copied.failure && copied.failedDatabaseName
      && copied.outcome !== MigrationOutcome.BulkCopyNotReady
      && copied.outcome !== MigrationOutcome.Cancelled) {
      reportCopyFailure(copied.failedDatabaseName, copied.failure, deps);
    }
    return {
      outcome: copied.outcome,
      databasesCopied: copied.databasesCopied,
      documentsCopied: copied.documentsCopied,
    };
  }

  // #5448: ask the engine question ONE more time, now the copy is over. Anything that
  // opens a database asks it, and the answer is fixed for the life of the page — so if
  // something asked while the copy was running, this session is committed to IndexedDB
  // and will keep reading and writing there. Believing the copy would then move the
  // device to SQLite on the next boot, without everything done after this moment.
  //
  // The copy is thrown away instead. Nothing is lost: the IndexedDB data is untouched,
  // the attempt is handed back (see `outcomeSpendsCopyBudget`), and a later boot — one
  // where nothing has asked yet — copies again.
  //
  // **#5449 keeps this for the SWITCH half and drops it from the background half**, and
  // the ticket proposed dropping it altogether. The reasoning it gave — the engine
  // marker is never written on the same page load as the copy — holds for the BULK copy
  // and not for the switch, which does run on the page load that writes the marker. The
  // window shrinks from minutes to seconds; it does not close. The image upload outbox
  // opens a database from its own connectivity subscription, which is exactly what
  // #5448 caught it doing. In the background half the question is meaningless: the app
  // is running, so the answer is always yes.
  if (deps.phase === MigrationPhase.Switch && deps.hasEngineBeenRead()) {
    return {
      outcome: MigrationOutcome.LatchTakenDuringCopy,
      databasesCopied: copied.databasesCopied,
      documentsCopied: copied.documentsCopied,
    };
  }

  return {
    outcome: MigrationOutcome.Completed,
    databasesCopied: copied.databasesCopied,
    documentsCopied: copied.documentsCopied,
    copiedDatabaseNames: copied.copiedDatabaseNames,
  };
}

/** What copying a whole list of databases produced. (#5270) */
export interface CopyDatabasesResult {
  outcome: MigrationOutcome;
  databasesCopied: number;
  documentsCopied: number;
  /** the databases copied and verified, by their SOURCE logical name */
  copiedDatabaseNames: string[];
  /** the per-database result behind a failure, so the caller can report it */
  failure?: CopyOneResult;
  /** which database failed, by its source logical name */
  failedDatabaseName?: string;
}

/**
 * Copy a named list of databases, one after another, and stop at the first that does not
 * finish. (#5270)
 *
 * Split out of {@link runSqliteMigrationAsync} so the reverse copy can reuse exactly this
 * loop rather than mirroring it by hand. It deliberately does NOT report: the two
 * directions file their failures under different problem names, so the caller reports
 * what this hands back.
 *
 * The list is enumerated by the caller, because the two directions cannot enumerate the
 * same way — the browser can list its own IndexedDB databases, and nothing can list the
 * native SQLite ones.
 */
export async function copyDatabasesAsync(
  names: readonly string[],
  deps: SqliteMigrationDeps,
): Promise<CopyDatabasesResult> {
  const direction = resolveCopyDirection(deps);
  let databasesCopied = 0;
  let documentsCopied = 0;
  const copiedDatabaseNames: string[] = [];

  for (const logicalName of names) {
    // #5449: write down that this device now has (or is about to have) a SQLite file
    // for this database, BEFORE anything is copied into it.
    //
    // The order is the point, and it is the same reasoning as spending the copy budget
    // up front. A boot killed between creating the file and recording it would leave a
    // database nothing on the device can see: the provider never held it, phase 4's
    // cleanup walks that same provider list, and sign-out is handed names rather than
    // sweeping. It would then sit there holding one user's data until somebody else
    // signed in. Recording a name for a file that never got created costs one delete
    // of something that is not there, which is not an error.
    //
    // #5270: only the forward direction has anything to record. A reverse copy's target
    // is an IndexedDB database, which the teardown enumerates and deletes like any other,
    // so there is no note for it to lose.
    if (direction.recordTargetName
      && !direction.recordTargetName(direction.targetNameFor(logicalName))) {
      // fail closed: see the note on that function. A target nothing recorded is a
      // target sign-out cannot delete, so it must not be created in the first place.
      return {
        outcome: MigrationOutcome.TargetRecordFailed,
        databasesCopied, documentsCopied, copiedDatabaseNames,
      };
    }

    // #5449: a partially filled target is NOT deleted here any more. Keeping it is what
    // lets a background copy resume from PouchDB's checkpoint rather than restart from
    // zero, which is the whole point of the ticket. `copyOneDatabaseAsync` decides
    // whether what is there can still be trusted.
    let result = await copyOneDatabaseAsync(logicalName, deps);
    if (result.outcome === MigrationOutcome.TargetRestarted) {
      // the target was thrown away because it could no longer vouch for itself. Copy
      // again on fresh handles — once, so a target that somehow keeps failing this test
      // cannot spin here.
      result = await copyOneDatabaseAsync(logicalName, deps);
    }
    if (result.outcome !== MigrationOutcome.Completed) {
      // handed back rather than reported here: the caller knows which direction this is,
      // and the two file their failures under different problem names
      return {
        outcome: result.outcome,
        databasesCopied, documentsCopied, copiedDatabaseNames,
        failure: result,
        failedDatabaseName: logicalName,
      };
    }

    databasesCopied += 1;
    documentsCopied += result.documentsCopied;
    copiedDatabaseNames.push(logicalName);
    console.log(`${LOG_PREFIX}copied and verified ${logicalName}`);
    deps.onProgress?.(databasesCopied, names.length);
  }

  return {
    outcome: MigrationOutcome.Completed, databasesCopied, documentsCopied, copiedDatabaseNames,
  };
}

/** A live follow that is running, and the way to stop it. (#5449) */
export interface SqliteFollowHandle {
  /**
   * Stop following and let go of the SQLite handles.
   *
   * Must be called before sign-out and before a page reload. A follow still running
   * while the teardown deletes its target would write into a database that is being
   * removed, and the failures it then reports describe nothing a person can act on.
   *
   * **Cancelling is what stops the writing, and it happens immediately.** Closing the
   * handles afterwards is tidy-up and is deliberately NOT waited for: each close is
   * bounded at a minute, they run one after another, and a wedged bridge would
   * otherwise stall a sign-out for several minutes on the one path whose contract is
   * that it always reaches the reload. The handles die with the page regardless.
   *
   * Safe to call twice and safe to call after the follow has already stopped.
   */
  stopAsync(): Promise<void>;
  /** how many databases are being followed — for the log line and the tests */
  readonly followedCount: number;
}

/**
 * Keep the SQLite copies current while the user carries on working. (#5449)
 *
 * **Why this exists at all.** The bulk copy takes minutes, and the app keeps running on
 * IndexedDB throughout. Everything written during those minutes — a workout logged, a
 * meal, a deletion, and equally everything the server pushes down during an ordinary
 * sync, because that also arrives as a write to IndexedDB — would otherwise be waiting
 * for the switch to carry it across. Following the source instead means SQLite is never
 * more than moments behind, and the switch has almost nothing left to do.
 *
 * It is PouchDB's own live replication, from the same checkpoint the bulk copy left, so
 * it starts where that finished rather than re-reading anything.
 *
 * Three things differ from the bulk copy, and each would be a bug if copied from it:
 *
 *  - **No silence bound.** A live replication is SUPPOSED to go quiet: somebody who is
 *    not touching their phone writes nothing for hours. The 60-second idle bound that
 *    protects the bulk copy would cancel this one on any idle device, which is every
 *    device, most of the time.
 *  - **Failures are watched for, not awaited.** Nothing is waiting on this promise, so
 *    an `error` that reached no one would leave the copy silently falling behind while
 *    the device still believed it had a current SQLite copy — and the switch would then
 *    trust a `verifiedThroughSeq` that no longer describes anything. It is reported at
 *    problem tier, under its own name.
 *  - **The SOURCE handles are left open and the TARGETS are closed on stop**, the same
 *    rule the copy obeys and for the same reason: PouchDB's IndexedDB adapter shares one
 *    browser connection per database name with the running app.
 */
export async function startFollowingSourcesAsync(
  deps: SqliteMigrationDeps,
  logicalNames: readonly string[],
): Promise<SqliteFollowHandle> {
  const direction = resolveCopyDirection(deps);
  const followNames = direction.followProblemNames ?? {
    failed: 'AndroidSqliteMigrationFollowFailed',
    denied: 'AndroidSqliteMigrationFollowDenied',
  };
  const handles: any[] = [];
  const targets: MigrationDb[] = [];

  for (const logicalName of logicalNames) {
    const source = deps.openDatabase(logicalName, direction.sourceAdapter);
    const target = deps.openDatabase(direction.targetNameFor(logicalName), direction.targetAdapter);
    targets.push(target);

    const handle: any = source.replicate.to(target, {
      live: true,
      retry: true,
      batch_size: MIGRATION_BACKGROUND_BATCH_SIZE,
      batches_limit: MIGRATION_BACKGROUND_BATCHES_LIMIT,
    });

    // Deliberately NOT awaited — a live replication only settles when it is cancelled.
    // What matters is that a failure is not swallowed: see the note above.
    handle?.on?.('error', (error: unknown) => {
      deps.reporter.trackSilentProblem(followNames.failed, {
        db_name: logicalName,
        ...errorReportFields(error),
      });
    });
    handle?.on?.('denied', (error: unknown) => {
      // `denied` is a single document the target refused, not the end of the
      // replication. It still means the SQLite copy is missing something the source
      // has, which the switch half's verification would later fail on — so it is worth
      // knowing about here, where the reason is still in hand.
      deps.reporter.trackSilentProblem(followNames.denied, {
        db_name: logicalName,
        ...errorReportFields(error),
      });
    });

    handles.push(handle);
  }

  console.log(`${LOG_PREFIX}following ${logicalNames.length} databases`);

  let stopped = false;
  return {
    followedCount: logicalNames.length,
    async stopAsync(): Promise<void> {
      if (stopped) return;
      stopped = true;
      // this is the part that matters, and it takes no time
      for (const handle of handles) cancelReplicationQuietly(handle);
      // tidy-up, deliberately not awaited — see the note on this method
      for (const target of targets) void closeQuietlyAsync(target, deps);
    },
  };
}

/**
 * Write down that this device has a SQLite file for this database. (#5449)
 *
 * Sign-out is the only thing that can delete these files, and it is handed a list of
 * names rather than sweeping a directory — the native plugin has no "list databases"
 * call. So a target nobody recorded survives sign-out with the previous user's
 * workouts and queued photos in it, waiting for whoever signs in next.
 *
 * **It fails CLOSED, and that is the whole reason it answers rather than returning
 * nothing.** If the note cannot be written — a browser with site data blocked throws
 * from the `localStorage` setter, and so does an exhausted quota — then copying anyway
 * would create a SQLite file that nothing on the device can ever delete. That is the
 * precise leak this record exists to prevent, so the caller stops instead: no note, no
 * copy. Not copying costs a device one background pass it will try again next launch.
 */
function recordTargetNameForSignOut(deps: SqliteMigrationDeps, logicalName: string): boolean {
  try {
    const updated = recordMigrationTargetName(
      deps.storage.getItem(SQLITE_MIGRATION_TARGETS_KEY), logicalName);
    // `undefined` means this name is already recorded — every boot re-opens the same
    // targets, so without that check this value would be rewritten on every pass
    if (updated !== undefined) deps.storage.setItem(SQLITE_MIGRATION_TARGETS_KEY, updated);
    return true;
  } catch (error) {
    deps.reporter.trackSilentProblem('AndroidSqliteMigrationTargetRecordFailed', {
      db_name: logicalName,
      ...errorReportFields(error),
    });
    return false;
  }
}

/** Report one database's failure under its own problem name, with no document contents. */
function reportCopyFailure(
  logicalName: string,
  result: CopyOneResult,
  deps: SqliteMigrationDeps,
): void {
  const problemName = MIGRATION_PROBLEM_NAMES[result.outcome] ?? 'AndroidSqliteMigrationFailed';
  deps.reporter.trackSilentProblem(problemName, {
    // the database NAME is fine to report; its contents never are
    db_name: logicalName,
    ...(result.attachmentCount === undefined ? {} : { attachment_count: result.attachmentCount }),
    ...(result.verification === undefined ? {} : {
      source_count: result.verification.sourceCount,
      target_count: result.verification.targetCount,
      difference_count: result.verification.differenceCount,
      // ids and how each differed — bounded, and never what a document said
      sample: result.verification.sampleReasons.join(','),
    }),
    ...errorReportFields(result.error),
  });
}
